import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, Truck, Loader2 } from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

interface Props {
  baProjectId: string | null;
}

const RISK_COLORS: Record<string, string> = {
  critical: "bg-destructive/15 text-destructive border-destructive/30",
  major: "bg-orange-100 text-orange-700 border-orange-300",
  minor: "bg-green-100 text-green-700 border-green-300",
};

const EMPTY_FORM = { name: "", category: "", risk_tier: "minor", last_audit_date: "", contact_email: "", notes: "" };

export default function SupplierPanel({ baProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);
  const [scarForm, setScarForm] = useState<Record<string, { description: string; due_date: string }>>({});
  const [scarOpen, setScarOpen] = useState<Record<string, boolean>>({});

  useEffect(() => { if (baProjectId) load(); }, [baProjectId]);

  const load = async () => {
    if (!baProjectId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/tools/suppliers?project_id=${baProjectId}`, { headers: authHeader });
      setSuppliers(data);
    } catch { setSuppliers([]); }
    finally { setLoading(false); }
  };

  const openNew = () => { setForm({ ...EMPTY_FORM }); setEditingId(null); setOpen(true); };
  const openEdit = (s: any) => {
    setForm({
      name: s.name || "", category: s.category || "", risk_tier: s.risk_tier || "minor",
      last_audit_date: s.last_audit_date ? s.last_audit_date.slice(0, 10) : "",
      contact_email: s.contact_email || "", notes: s.notes || "",
    });
    setEditingId(s.id);
    setOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim() || !baProjectId) return;
    setSaving(true);
    try {
      const body = {
        project_id: baProjectId, name: form.name, category: form.category || undefined,
        risk_tier: form.risk_tier, last_audit_date: form.last_audit_date || undefined,
        contact_email: form.contact_email || undefined, notes: form.notes || undefined,
      };
      if (editingId) {
        await axios.patch(`${BASE}/api/tools/suppliers/${editingId}`, body, { headers: authHeader });
      } else {
        await axios.post(`${BASE}/api/tools/suppliers`, body, { headers: authHeader });
      }
      setOpen(false);
      load();
    } catch (e: any) { alert(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this supplier and all SCARs?")) return;
    await axios.delete(`${BASE}/api/tools/suppliers/${id}`, { headers: authHeader });
    load();
  };

  const handleAddScar = async (supplierId: string) => {
    const sf = scarForm[supplierId];
    if (!sf?.description?.trim()) return;
    await axios.post(`${BASE}/api/tools/suppliers/${supplierId}/scars`, {
      supplier_id: supplierId, description: sf.description, due_date: sf.due_date || undefined,
    }, { headers: authHeader });
    setScarForm(f => ({ ...f, [supplierId]: { description: "", due_date: "" } }));
    setScarOpen(o => ({ ...o, [supplierId]: false }));
    load();
  };

  const handleResolveScar = async (scarId: string) => {
    await axios.patch(`${BASE}/api/tools/scars/${scarId}/resolve`, {}, { headers: authHeader });
    load();
  };

  if (!baProjectId) {
    return (
      <div className="p-6 text-center text-sm text-muted-foreground">
        <Truck className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
        Visit Requirements tab first to initialise the project backend.
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 max-w-3xl">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-sm font-mono text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
            <Truck className="h-4 w-4" /> Approved Supplier List
          </h2>
          <p className="text-xs text-muted-foreground mt-0.5">ISO 13485 §7.4 — Purchasing Controls</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="gap-1.5" onClick={openNew}><Plus className="h-3.5 w-3.5" /> Add Supplier</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">{editingId ? "Edit Supplier" : "Add Supplier"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 pt-2">
              <Input placeholder="Supplier name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input placeholder="Category (e.g. software, lab)" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} />
                <Select value={form.risk_tier} onValueChange={v => setForm({ ...form, risk_tier: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="critical">Critical</SelectItem>
                    <SelectItem value="major">Major</SelectItem>
                    <SelectItem value="minor">Minor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Last Audit Date</p>
                  <Input type="date" value={form.last_audit_date} onChange={e => setForm({ ...form, last_audit_date: e.target.value })} />
                </div>
                <Input placeholder="Contact email" value={form.contact_email} onChange={e => setForm({ ...form, contact_email: e.target.value })} />
              </div>
              <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} />
              <Button onClick={handleSave} disabled={!form.name.trim() || saving} className="w-full gap-1.5">
                {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</> : editingId ? "Save Changes" : "Add Supplier"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {loading && <p className="text-xs text-muted-foreground animate-pulse">Loading…</p>}

      {!loading && suppliers.length === 0 && (
        <div className="text-center py-12 border border-border rounded-md bg-secondary/10">
          <Truck className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
          <p className="text-sm font-semibold">No suppliers yet</p>
          <p className="text-xs text-muted-foreground mt-1">Add your first approved supplier above.</p>
        </div>
      )}

      <div className="space-y-2">
        {suppliers.map(s => {
          const isOpen = expanded[s.id];
          const openScars = (s.scars || []).filter((sc: any) => sc.status === "open");
          return (
            <div key={s.id} className="border border-border rounded-md bg-card overflow-hidden">
              <div className="p-3 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-semibold text-card-foreground">{s.name}</span>
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${RISK_COLORS[s.risk_tier] || ""}`}>{s.risk_tier}</Badge>
                    {s.category && <span className="text-[10px] text-muted-foreground font-mono">{s.category}</span>}
                    {openScars.length > 0 && (
                      <Badge variant="outline" className="text-[10px] px-1.5 bg-orange-100 text-orange-700 border-orange-300 flex items-center gap-0.5">
                        <AlertTriangle className="h-2.5 w-2.5" /> {openScars.length} SCAR{openScars.length > 1 ? "s" : ""}
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-3 text-[11px] text-muted-foreground font-mono mt-0.5 flex-wrap">
                    {s.last_audit_date && <span>audited: {s.last_audit_date.slice(0, 10)}</span>}
                    {s.contact_email && <span>{s.contact_email}</span>}
                    <span>{(s.scars || []).length} SCAR{(s.scars || []).length !== 1 ? "s" : ""} total</span>
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" onClick={() => openEdit(s)}>Edit</Button>
                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-destructive border-destructive/30" onClick={() => handleDelete(s.id)}>Delete</Button>
                  <button
                    onClick={() => setExpanded(e => ({ ...e, [s.id]: !e[s.id] }))}
                    className="h-7 w-7 flex items-center justify-center border border-input rounded-md hover:bg-accent/10"
                  >
                    {isOpen ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              {isOpen && (
                <div className="border-t border-border p-3 space-y-2 bg-secondary/10">
                  <div className="flex items-center justify-between">
                    <p className="text-[11px] font-mono font-semibold text-muted-foreground uppercase tracking-wider">SCARs</p>
                    <button
                      onClick={() => setScarOpen(o => ({ ...o, [s.id]: !o[s.id] }))}
                      className="text-[10px] text-accent underline hover:no-underline"
                    >
                      {scarOpen[s.id] ? "Cancel" : "+ Add SCAR"}
                    </button>
                  </div>

                  {scarOpen[s.id] && (
                    <div className="flex gap-2 flex-wrap">
                      <Input
                        placeholder="SCAR description *"
                        className="flex-1 h-7 text-xs"
                        value={scarForm[s.id]?.description || ""}
                        onChange={e => setScarForm(f => ({ ...f, [s.id]: { ...f[s.id], description: e.target.value, due_date: f[s.id]?.due_date || "" } }))}
                      />
                      <Input
                        type="date"
                        className="w-36 h-7 text-xs"
                        value={scarForm[s.id]?.due_date || ""}
                        onChange={e => setScarForm(f => ({ ...f, [s.id]: { ...f[s.id], due_date: e.target.value, description: f[s.id]?.description || "" } }))}
                      />
                      <Button size="sm" className="h-7 px-2 text-[11px]" onClick={() => handleAddScar(s.id)}>Add</Button>
                    </div>
                  )}

                  {(s.scars || []).length === 0 && (
                    <p className="text-[11px] text-muted-foreground">No SCARs filed for this supplier.</p>
                  )}

                  {(s.scars || []).map((sc: any) => (
                    <div key={sc.id} className="border border-border rounded bg-card p-2 flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-card-foreground">{sc.description}</p>
                        <div className="flex gap-2 text-[10px] text-muted-foreground font-mono mt-0.5">
                          {sc.due_date && <span>due: {sc.due_date.slice(0, 10)}</span>}
                          {sc.resolved_at && <span>resolved: {new Date(sc.resolved_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <Badge variant="outline" className={`text-[10px] px-1.5 ${sc.status === "resolved" ? "bg-green-100 text-green-700 border-green-300" : "bg-yellow-100 text-yellow-700 border-yellow-300"}`}>
                          {sc.status}
                        </Badge>
                        {sc.status === "open" && (
                          <Button size="sm" variant="outline" className="h-6 px-1.5 text-[10px] text-green-700 border-green-300" onClick={() => handleResolveScar(sc.id)}>
                            <CheckCircle2 className="h-2.5 w-2.5 mr-0.5" /> Resolve
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
